import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Container,
  Box,
  Paper,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Avatar,
  Stack,
} from '@mui/material';
import {
  TrendingUp,
  Api as ApiIcon,
  People,
  Speed,
  CheckCircleOutline,
  ErrorOutline,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { userEndpoints } from '../config/api';

const Dashboard = () => {
  const { user, isAuthenticated, getAccessToken } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [userUsagePlan, setUserUsagePlan] = useState(null);
  const [availableUsagePlans, setAvailableUsagePlans] = useState([]);
  const [apiUsage, setApiUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  const userId = user?.attributes?.sub;

  const fetchApiKey = useCallback(async () => {
    if (!userId) return;
    try {
      const token = await getAccessToken();
      const response = await fetch(`${userEndpoints.apiKey}?user-id=${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Error fetching API key');
      const data = await response.json();
      setApiKey(data.apiKey || '');
    } catch (error) {
      console.error('Error fetching API key:', error);
      setApiKey('');
    }
  }, [userId]);

  const fetchUserUsagePlan = useCallback(async () => {
    if (!userId) return;
    try {
      const token = await getAccessToken();
      const response = await fetch(`${userEndpoints.usagePlan}?user-id=${userId}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Error fetching usage plan');
      const data = await response.json();
      setUserUsagePlan(data.usagePlan);
    } catch (error) {
      console.error('Error fetching usage plan:', error);
    }
  }, [userId]);

  const fetchApiUsage = useCallback(async () => {
    if (!userId) return;
    try {
      const token = await getAccessToken();
      const response = await fetch(`${userEndpoints.usage}?user-id=${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Error fetching API usage');
      const data = await response.json();
      setApiUsage(data.usage);
    } catch (error) {
      console.error('Error fetching API usage:', error);
    }
  }, [userId]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchApiKey();
      await fetchUserUsagePlan();
      await fetchApiUsage();
      setLoading(false);
    };

    if (userId) {
      loadData();
    }
  }, [userId]);

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ marginTop: 4 }}>
        <Typography variant="h4" gutterBottom>
          Welcome, {user?.username}!
        </Typography>

        <Paper elevation={3} sx={{ padding: 3, marginTop: 3 }}>
          <Typography variant="h6" gutterBottom>
            Your API Key
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: 2, wordBreak: 'break-all' }}>
            {apiKey || 'No API key available. Request one below.'}
          </Typography>
        </Paper>

        <Paper elevation={3} sx={{ padding: 3, marginTop: 3 }}>
          <Typography variant="h6" gutterBottom>
            API Usage Statistics
          </Typography>
          {apiUsage ? (
            <>
              <Typography variant="body1">Total Requests: {apiUsage.totalRequests || 0}</Typography>
              <Typography variant="body1">Requests Today: {apiUsage.requestsToday || 0}</Typography>
              <Typography variant="body1">Requests This Month: {apiUsage.requestsThisMonth || 0}</Typography>
              <Typography variant="body1">Last Request: {apiUsage.lastRequestTime || 'N/A'}</Typography>
            </>
          ) : (
            <Typography variant="body1">No usage data available</Typography>
          )}
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Dashboard;
